const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface WeeklyTimeInterval {
  daysOfWeek?: number[];
  startTime?: string;
  endTime?: string;
}

interface ExpandedInterval {
  end: number;
  ruleIndex: number;
  start: number;
}

export function parseTimeMinutes(value: unknown): number | null {
  if (typeof value !== "string" || !TIME_PATTERN.test(value)) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours! * 60 + minutes!;
}

export function isOvernightInterval(interval: WeeklyTimeInterval): boolean {
  const start = parseTimeMinutes(interval.startTime);
  const end = parseTimeMinutes(interval.endTime);
  return start !== null && end !== null && end < start;
}

export function matchesWeeklyTimeInterval(
  interval: WeeklyTimeInterval,
  weekday: number,
  minute: number,
): boolean {
  const start = parseTimeMinutes(interval.startTime);
  const end = parseTimeMinutes(interval.endTime);
  if (start === null || end === null || start === end) return false;

  const days = interval.daysOfWeek || [];
  if (start < end) {
    return days.includes(weekday) && minute >= start && minute < end;
  }

  const previousWeekday = (weekday + 6) % 7;
  return (
    (days.includes(weekday) && minute >= start) ||
    (days.includes(previousWeekday) && minute < end)
  );
}

export function findOverlappingWeeklyIntervals(
  intervals: WeeklyTimeInterval[],
): { firstRuleIndex: number; secondRuleIndex: number } | null {
  const expanded: ExpandedInterval[] = [];
  intervals.forEach((interval, ruleIndex) => {
    const startMinute = parseTimeMinutes(interval.startTime);
    const endMinute = parseTimeMinutes(interval.endTime);
    if (startMinute === null || endMinute === null || startMinute === endMinute)
      return;

    [...new Set(interval.daysOfWeek || [])].forEach((day) => {
      const start = day * MINUTES_PER_DAY + startMinute;
      const end =
        day * MINUTES_PER_DAY +
        endMinute +
        (endMinute < startMinute ? MINUTES_PER_DAY : 0);
      expanded.push({ end, ruleIndex, start });
    });
  });

  for (let leftIndex = 0; leftIndex < expanded.length; leftIndex += 1) {
    const left = expanded[leftIndex]!;
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < expanded.length;
      rightIndex += 1
    ) {
      const right = expanded[rightIndex]!;
      if (left.ruleIndex === right.ruleIndex) continue;

      for (const weekOffset of [-MINUTES_PER_WEEK, 0, MINUTES_PER_WEEK]) {
        const shiftedStart = right.start + weekOffset;
        const shiftedEnd = right.end + weekOffset;
        if (
          Math.max(left.start, shiftedStart) < Math.min(left.end, shiftedEnd)
        ) {
          return {
            firstRuleIndex: left.ruleIndex,
            secondRuleIndex: right.ruleIndex,
          };
        }
      }
    }
  }
  return null;
}
